import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';

export type LoadingSpinnerProps = {
  message?: string;
  size?: 'small' | 'medium' | 'large';
  fullScreen?: boolean;
  className?: string;
};

const SIZE_MAP = {
  small: 'w-6 h-6',
  medium: 'w-12 h-12',
  large: 'w-16 h-16',
} as const;

/**
 * Themed loading spinner with optional message. Use `fullScreen` for page overlays.
 */
export function LoadingSpinner({
  message = 'Loading...',
  size = 'medium',
  fullScreen = false,
  className = '',
}: LoadingSpinnerProps) {
  const { colors } = useTheme();
  const spinnerSize = SIZE_MAP[size] ?? SIZE_MAP.medium;

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-4',
        fullScreen
          ? 'fixed inset-0 z-50 bg-white/90 dark:bg-gray-900/90'
          : 'py-8',
        className,
      )}
    >
      <div className={cn('relative', spinnerSize)}>
        <div className={cn('absolute inset-0 rounded-full border-4', spinnerSize, colors.border)} />
        <div
          className={cn(
            'absolute inset-0 animate-spin rounded-full border-4 border-t-transparent',
            spinnerSize,
            colors.primary,
          )}
        />
      </div>
      {message ? (
        <p className={cn('text-sm font-medium', colors.textSecondary)}>{message}</p>
      ) : null}
    </div>
  );
}

export default LoadingSpinner;
